<template>
  <header class="owc-header">
    <ValidationBoxWrapper v-if="isEditMode">
      <slot slot="validation-box" name="validation-box" />
    </ValidationBoxWrapper>
    <div class="owc-header__wrapper">
      <a v-if="skipToContentLabel" class="owc-header__skip-to-content" :href="`#${firstPageComponentId}`">
        {{ skipToContentLabel }}
      </a>
      <nav class="owc-header__top">
        <template v-if="shouldShowMainNavigation">
          <!--TODO: Missing Mobile AriaLabels -->
          <OwcHeaderButton
            v-for="menuButton in headerElements"
            :key="menuButton.id"
            :icon="menuButton.iconLabel"
            :aria-label="menuButton.id === headerModelId ? mobileButtonLabel : 'menu'"
            :class="menuButton.class"
            :is-icon-small="true"
            @click.native="handleHeaderButtonClick($event, menuButton.id)"
          />
        </template>

        <!-- Wishlist should be displayed on the left side in mobile view -->
        <SlotContainer
          v-if="shouldShowLegacyWishlistMobile"
          class="owc-header__item-favorites"
          refName="slot-sccs-wishlist-icon"
          slotName="slot-sccs-wishlist-icon"
        >
          <slot slot="slot-sccs-wishlist-icon" name="slot-sccs-wishlist-icon"></slot>
        </SlotContainer>
        <SlotContainer
          v-if="shouldShowWishlistMobile"
          class="owc-header__item-favorites"
          refName="slot-seamless-eqpodc-flyout"
          slotName="seamless-eqpodc-flyout"
        >
          <slot slot="seamless-eqpodc-flyout" name="seamless-eqpodc-flyout"></slot>
        </SlotContainer>

        <!-- Region Picker -->
        <SlotContainer
          v-if="shouldShowPickerMobile"
          class="owc-header__item-picker"
          refName="slot-seamless-dh-io-owcc-region-picker"
          slotName="seamless-dh-io-owcc-region-picker"
        >
          <slot slot="seamless-dh-io-owcc-region-picker" name="seamless-dh-io-owcc-region-picker"></slot>
        </SlotContainer>

        <!-- MB logo -->
        <OwcLogo :homePageLinkObject="homePageLink" />

        <div class="owc-header__language-and-imprint">
          <a v-if="imprint" class="owc-header__item-imprint" :target="imprint.target" :href="imprint.url">
            {{ imprint.label }}
          </a>
          <OwcLanguageSwitch
            v-if="languageSwitch && languageSwitch.length > 1"
            class="owc-header__item-lang"
            :languageSwitch="languageSwitch"
          />
        </div>

        <div class="owc-header__top-menu-tools">
          <SlotContainer
            v-if="shouldShowSearchBar"
            class="owc-header__search owc-header__item-search"
            refName="slot-fss-search-input"
            slotName="fss-search-input"
          >
            <slot slot="fss-search-input" name="fss-search-input"></slot>
          </SlotContainer>

          <div class="owc-header__item-container">
            <SlotContainer
              v-if="shouldShowPickerDesktop"
              class="owc-header__item-picker"
              refName="slot-seamless-dh-io-owcc-region-picker"
              slotName="seamless-dh-io-owcc-region-picker"
            >
              <slot slot="seamless-dh-io-owcc-region-picker" name="seamless-dh-io-owcc-region-picker"></slot>
            </SlotContainer>
            <SlotContainer
              v-if="shouldShowLegacyWishlistDesktop"
              class="owc-header__item-favorites"
              refName="slot-sccs-wishlist-icon"
              slotName="slot-sccs-wishlist-icon"
            >
              <slot slot="slot-sccs-wishlist-icon" name="slot-sccs-wishlist-icon"></slot>
            </SlotContainer>
            <SlotContainer
              v-if="shouldShowWishlistDesktop"
              class="owc-header__item-favorites"
              refName="slot-seamless-eqpodc-flyout"
              slotName="seamless-eqpodc-flyout"
            >
              <slot slot="seamless-eqpodc-flyout" name="seamless-eqpodc-flyout"></slot>
            </SlotContainer>

            <SlotContainer
              v-if="shouldShowReachMe"
              class="owc-header__item-msg"
              refName="slot-reach-me"
              slotName="slot-reach-me"
            >
              <slot slot="slot-reach-me" name="slot-reach-me"></slot>
            </SlotContainer>

            <SlotContainer
              v-if="shouldShowShoppingCart"
              class="owc-header__shopping-cart"
              refName="slot-seamless-shopping-cart"
              slotName="seamless-shopping-cart"
            >
              <slot slot="seamless-shopping-cart" name="seamless-shopping-cart"></slot>
            </SlotContainer>

            <SlotContainer
              v-if="shouldShowB2XUserMenu"
              refName="slot-seamless-iam-user-menu"
              slotName="seamless-iam-user-menu"
              class="owc-header__item-profile"
            >
            </SlotContainer>
          </div>
        </div>
      </nav>
      <template v-if="shouldShowMainNavigation">
        <SlotContainer
          v-if="isEditMode"
          class="owc-header__header-navigation"
          elementDOM="nav"
          refName="slot-header-navigation"
          :slot-component-data="JSON.stringify({ languageSwitch, imprint, currentMq })"
        >
          <slot></slot>
        </SlotContainer>
        <nav v-else class="owc-header__header-navigation">
          <OwcHeaderMainNavigation
            v-if="componentData.mainNavigation"
            :datajson="publishJson"
            :currentMq="currentMq"
          >
            <slot slot="fss-search-input" name="fss-search-input"></slot>
            <slot slot="seamless-vmos-flyout" name="seamless-vmos-flyout"></slot>
          </OwcHeaderMainNavigation>
        </nav>
        <div
          v-if="isAnyFlyoutOpen"
          :class="['owc-header-mask', interceptClass]"
          @click="handleFlyoutMaskClick($event)"
        />
      </template>
    </div>
  </header>
</template>

<script lang="ts">
/**
 * TODO: Documentation
 * `Header` - .
 */
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import { FIRST_PAGE_COMPONENT } from '@owc-utils/Elements/SelectorPaths';
import { MediaQueryChangeEvent } from '@owc-utils/MediaQueryService/MediaQueryChangeEvent';
import getAccessibilityStackInstance from '@owc-utils/common/AccessibilityFocusStackHelper';
import headerMixin from '@owc-utils/Mixins/Header';
import owcDataComponent from '@owc-utils/Mixins/OwcDataComponent';
import { lockWindowScroll, unlockWindowScroll } from '@owc-utils/common/LockWindow';

import {
  HeaderComponent,
  HeaderComputed,
  HeaderData,
  HeaderItem,
  HeaderMethods,
} from '@owc-packages/Header/src/Header';
import {
  HEADER_MOBILE_MENUS,
  HEADER_MAIN_NAV_FLYOUT,
  FOCUS_STACK_HEADER_CONTEXT,
  HEADER_JUMP_TO_CONTENT_ID,
  IMPORT_MAP_CANDIDATES,
} from '@owc-packages/Header/src/Header.configs';

import { mobileBreakpoints, desktopBreakpoints } from '@owc-utils/MediaQueryService/MediaQueryUtils';
import SlotContainer from '@owc-packages/SlotContainer/src/SlotContainer.vue';
import OwcLanguageSwitch from '@owc-packages/Header/src/LanguageSwitch.vue';
import OwcLogo from '@owc-packages/Header/src/HeaderLogo.vue';
import OwcHeaderButton from '@owc-packages/Header/src/HeaderButton.vue';
import OwcHeaderMainNavigation from '@owc-packages/Header/src/HeaderMainNavigation.vue';
import ValidationBoxWrapper from '@owc-packages/ValidationBox/src';
import { HeaderTopItems } from './Header.enums';
import { Props } from '../../_types/ComponentInitializeDispatcher';
import { updateActiveFlyout, initializeHeaderIntegrations } from '@owc-utils/Seamless/headerIntegrations';
import { MaskBlockEvent } from '@owc-utils/Seamless/headerIntegrations.d';
import { HeaderEvents } from '../../_utils/Seamless/headerEvents.enum';
import Logger from '../../_utils/common/Logger';

const globalFocusStack = getAccessibilityStackInstance();

export default (ComponentInitializeDispatcher as HeaderComponent).extend<
  HeaderData,
  HeaderMethods,
  HeaderComputed,
  Props
>({
  name: 'owc-header',
  components: {
    OwcLanguageSwitch,
    OwcHeaderMainNavigation,
    OwcLogo,
    OwcHeaderButton,
    SlotContainer,
    ValidationBoxWrapper,
  },
  mixins: [headerMixin, owcDataComponent],
  data() {
    return {
      currentMq: 'mq1',
      mobileButtonLabel: '',
      nativePageLanguage: '',
      isAnyFlyoutOpen: false,
      flyoutBlockClose: false,
      firstPageComponentId: HEADER_JUMP_TO_CONTENT_ID,
    };
  },
  created() {
    this.interceptClass = 'owc-header-flyout-intercept';
    this.headerModelId = HeaderTopItems.HEADER_MOBILE_MENU;
  },
  beforeMount() {
    this.currentMq = this.getCurrentMediaQuery();
  },
  updated() {
    this.moveWishlistModuleToLeft();
  },
  watch: {
    isAnyFlyoutOpen(isOpen, previousOpenState) {
      if (isOpen) {
        lockWindowScroll();
      } else {
        unlockWindowScroll();

        if (previousOpenState) {
          this.flyoutBlockClose = false;
        }
      }
    },
    shouldShowWishlistMobile: {
      handler() {
        this.moveWishlistModuleToLeft();
      },
      immediate: true,
    },
  },
  mounted() {
    if (this.onMQChange) {
      this.onMQChange(this.resizeEventHandler as EventListenerOrEventListenerObject);
    }

    this.onHeaderStateChange(state => {
      this.mobileButtonLabel = state.navigationLabels?.mobileMenuButton;
      this.isAnyFlyoutOpen = Boolean(state.flyoutChain?.length);
    });

    this.setHeaderElement(this.$el);
    //FIXME will be done in later stages for this feature
    initializeHeaderIntegrations(this);

    this.firstWebComponent();

    this.$el.addEventListener(
      HeaderEvents.FLYOUT_BLOCK_CLOSE,
      ({ detail: { shouldBlockClose } }: CustomEvent<MaskBlockEvent>) =>
        (this.flyoutBlockClose = shouldBlockClose),
    );

    if (this.visibilityOption) {
      Object.keys(this.visibilityOption).forEach(key => {
        if (IMPORT_MAP_CANDIDATES.has(key)) {
          this.importModuleIfVisible(key);
        }
      });
    }
  },
  computed: {
    languageSwitch() {
      return this.componentData?.languageSwitch;
    },
    imprint() {
      return this.componentData?.imprint;
    },
    publishJson() {
      return JSON.stringify(
        Object.assign(
          { languageSwitch: this.languageSwitch, imprint: this.imprint },
          this.componentData?.mainNavigation,
        ),
      );
    },
    visibilityOption() {
      return this.componentData?.visibilitySettings;
    },
    shouldShowMainNavigation() {
      return Boolean(this.visibilityOption?.mainNavigationEnabled);
    },
    shouldShowSearchBar() {
      return Boolean(this.visibilityOption?.searchBarEnabled);
    },
    shouldShowMercedesMe() {
      return Boolean(this.visibilityOption?.mercedesMeEnabled);
    },
    shouldShowB2XUserMenu() {
      return Boolean(this.visibilityOption?.userMenuEnabled);
    },
    shouldShowShoppingCart() {
      return Boolean(this.visibilityOption?.shoppingCartEnabled);
    },
    shouldShowWishlistMobile() {
      return (
        this.shouldShowShoppingCart &&
        Boolean(this.visibilityOption?.wishlistEnabled) &&
        !desktopBreakpoints.includes(this.currentMq)
      );
    },
    shouldShowWishlistDesktop() {
      const isDisplayed = this.shouldShowWishlistMobile;
      return !isDisplayed && Boolean(this.visibilityOption?.wishlistEnabled);
    },
    shouldShowLegacyWishlistMobile() {
      return (
        Boolean(this.visibilityOption?.legacyWishlistEnabled) && !desktopBreakpoints.includes(this.currentMq)
      );
    },
    shouldShowLegacyWishlistDesktop() {
      return (
        Boolean(this.visibilityOption?.legacyWishlistEnabled) && desktopBreakpoints.includes(this.currentMq)
      );
    },
    shouldShowReachMe() {
      return Boolean(this.visibilityOption?.reachMeEnabled) && !mobileBreakpoints.includes(this.currentMq);
    },
    shouldShowPickerDesktop() {
      return (
        Boolean(this.visibilityOption?.regionPickerEnabled) && desktopBreakpoints.includes(this.currentMq)
      );
    },
    shouldShowPickerMobile() {
      return (
        Boolean(this.visibilityOption?.regionPickerEnabled) && !desktopBreakpoints.includes(this.currentMq)
      );
    },
    homePageLink() {
      return this.componentData?.homePageLink;
    },
    skipToContentLabel() {
      return this.componentData?.skipToContentLabel;
    },
    headerElements() {
      const shouldShowPicker = this.shouldShowPickerMobile;

      return HEADER_MOBILE_MENUS.filter(
        (headerItem: HeaderItem) =>
          !(shouldShowPicker && headerItem.id === HeaderTopItems.HEADER_MODELS_MENU),
      );
    },
  },
  methods: {
    resizeEventHandler(resizeEvent: CustomEvent<MediaQueryChangeEvent>) {
      this.currentMq = resizeEvent.detail.current;
    },
    handleHeaderButtonClick(event: Event, buttonId: string) {
      const focusedElement = event.target as HTMLElement;
      this.setModelsFlyoutState(false);

      if (focusedElement) {
        globalFocusStack.pushToFocusStack(focusedElement, FOCUS_STACK_HEADER_CONTEXT);
      }

      const flyoutState = this.getOpenFlyoutId();
      const chain = [{ infoToAdd: HEADER_MAIN_NAV_FLYOUT, flyoutLevel: 1 }];

      updateActiveFlyout(this.$el, HEADER_MAIN_NAV_FLYOUT, Boolean(flyoutState));

      switch (buttonId) {
        case 'modelsMenu': {
          if (flyoutState === buttonId) {
            // repeated clicks close button
            this.closeHeaderFlyout(true);
            this.setOpenFlyoutId(null);
          } else {
            chain.push({ infoToAdd: this.getModelTopicId(), flyoutLevel: 2 });

            // if other flyout is open, close it and open models
            this.closeHeaderFlyout(true);
            this.openHeaderFlyout(chain);
            this.addToTrackingTree(this.getModelTopicId(), 2);
            this.setOpenFlyoutId(buttonId);
            this.setModelsFlyoutState(true);
          }
          break;
        }
        default: {
          if (flyoutState === buttonId) {
            // repeated clicks close button
            this.closeHeaderFlyout(true);
            this.setOpenFlyoutId(null);
          } else {
            // if other flyout is open and user clicks on
            // main, close it
            this.closeHeaderFlyout(false);
            this.openHeaderFlyout(chain);
            this.setOpenFlyoutId(buttonId);
          }
          break;
        }
      }
    },
    handleFlyoutMaskClick(event) {
      if (event.target.classList.contains(this.interceptClass) && !this.flyoutBlockClose) {
        this.closeHeaderFlyout(true);
        this.setOpenFlyoutId(null);
        this.isAnyFlyoutOpen = false;
        updateActiveFlyout(null, null, false);
      }
    },
    firstWebComponent() {
      return document.querySelector(FIRST_PAGE_COMPONENT)?.setAttribute('id', this.firstPageComponentId);
    },
    importModuleIfVisible(moduleName: string) {
      const tenantComponentName = IMPORT_MAP_CANDIDATES.get(moduleName) as string;

      if (tenantComponentName) {
        // polyfil support while importmap has unsupported iOS versions https://caniuse.com/?search=importmap
        if (Object.hasOwn(globalThis, 'importShim')) {
          // import module using es-module-shims npm package
          globalThis.importShim(tenantComponentName).catch(error => {
            // module doesn't exist in importmap
            Logger.error(`Module ${tenantComponentName} failed to import (importmaps)`, error);
          });
        } else if (Object.hasOwn(globalThis, 'importModule')) {
          globalThis.importModule(tenantComponentName).catch(error => {
            // module doesn't exist in importmap
            Logger.error(`Module ${tenantComponentName} failed to import (importmaps)`, error);
          });
        }
      }
    },
    moveWishlistModuleToLeft() {
      const flyout = document.querySelector('[slot="seamless-eqpodc-flyout"]');
      const secondaryFlyout = flyout?.shadowRoot?.querySelector('owc-header-secondary-flyout');

      if (this.shouldShowWishlistMobile === true) {
        secondaryFlyout?.setAttribute('is-left-aligned', 'true');
      } else {
        secondaryFlyout?.removeAttribute('is-left-aligned');
      }
    },
  },
});
</script>

<style lang="scss">
@import '@owc-styles/utils';
@import './styles/flyout-vars';

$header-tools: 'imprint', 'lang', 'search', 'favorites', 'profile', 'cart', 'msg' 'picker';
$jumpToContentTransitionDuration: 400ms;

@each $item in $header-tools {
  .owc-header__item-#{$item} {
    display: none;
  }
}

.owc-header {
  position: relative;

  $header-gap-size: $button-clickable-area;

  z-index: $z-index-header;

  width: 100%;

  background-color: var(--wb-black);

  &__top {
    @include wb-content-width;
    position: relative;

    display: flex;
    align-items: center;
    justify-items: stretch;

    height: $header-mobile-height;
    padding: var(--wb-spacing-xs) 0;

    @include breakpoint-from(mq3) {
      height: $flyout-tablet-header-height;
    }
    @include breakpoint-from(mq5) {
      align-items: center;

      padding: 16px 0;
    }
  }
  &__top-menu-tools {
    display: flex;
    flex: 1;
    justify-content: flex-end;

    @include breakpoint-from(mq3) {
      flex: initial;
    }
  }
  &__item-logo {
    display: flex;
  }
  &__header-navigation {
    @include breakpoint-from(mq3) {
      position: relative;
    }
    @include breakpoint-from(mq5) {
      border-top: 1px solid var(--wb-grey-15);
    }
  }
  &__item-imprint {
    @include wb-type-hint;

    justify-self: center;

    margin: 0 calc(var(--wb-spacing-xs) / 2);

    color: var(--wb-grey-70);

    text-decoration: none;
  }
  &__item-search {
    display: none;
  }
  &__item-favorites,
  &__item-profile,
  &__item-picker,
  &__item-cart {
    display: flex;
  }
  &__language-and-imprint {
    display: none;
    flex: 1;
    align-items: center;

    @include breakpoint-from(mq5) {
      justify-content: flex-end;

      margin: 0 calc(var(--wb-spacing-xs) / 2);
    }
  }
  &__item-container {
    display: flex;

    &:not(:empty) {
      margin-left: calc(var(--wb-spacing-xs) / 2);
    }
  }
  &__skip-to-content {
    @include wb-type-copy;

    position: absolute;
    top: var(--wb-spacing-xl);

    z-index: $z-index-default;

    display: none;
    padding: var(--wb-spacing-xs);

    color: var(--wb-white);
    text-decoration: none;

    background-color: var(--wb-black);

    transform: translateX(-100%);

    transition: transform $jumpToContentTransitionDuration ease-in-out;
  }

  @include breakpoint-from(mq3) {
    &__item-search,
    &__item-lang,
    &__item-msg,
    &__language-and-imprint {
      display: flex;
    }
    &__skip-to-content {
      display: block;

      &:focus {
        transform: translateX(0);
      }
    }
    &__item-cart,
    &__item-favorites,
    &__item-picker,
    &__item-profile {
      min-width: 48px;
      min-height: 48px;
    }
  }
  @include breakpoint-from(mq5) {
    &__item-menu {
      display: none;
    }
    &__item-imprint {
      display: block;
    }
  }

  &-mask {
    @include breakpoint-from(mq3) {
      position: fixed;
      top: $flyout-tablet-header-height;
      left: 0;

      z-index: $z-index-default;

      width: 100%;
      height: calc(100% - #{$flyout-tablet-header-height});

      background-color: var(--wb-black);

      opacity: $flyout-animation-opacity;

      content: '';
    }
    @include breakpoint-from(mq5) {
      top: $flyout-desktop-header-height;

      height: calc(100% - #{$flyout-desktop-header-height});
    }
  }
}

@include rtl-rule('.owc-header__skip-to-content', false) {
  transform: translateX(100%);

  @include breakpoint-from(mq3) {
    &:focus {
      transform: translateX(0);
    }
  }
}
</style>
